/**
 * F-298 / T-240-003 — LinkedIn Texas organic engine dashboard.
 *
 * Surfaces the daily organic post pipeline on LP (/api/linkedin/texas/*):
 * pick a date, generate/reuse the daily post, drive the human review gate
 * (approve -> publish -> reject), record per-template engagement, and view
 * the rotation performance + meta. There is no per-post status route — the
 * gate is date + action against the Texas rotation.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
    linkedinOrganicApi,
    LinkedInTexasPost,
    LinkedInPerformanceRow,
    LinkedInTexasMeta,
    LinkedInReviewAction,
    pendingActions,
    statusBadgeClass,
} from '../../services/linkedinOrganic';

const today = (): string => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};

interface MetricsDraft {
    views: string;
    clicks: string;
    conversions: string;
}

const emptyMetrics = (): MetricsDraft => ({ views: '', clicks: '', conversions: '' });

export const LinkedInOrganicDashboard: React.FC = () => {
    const [date, setDate] = useState(today());
    const [post, setPost] = useState<LinkedInTexasPost | null>(null);
    const [performance, setPerformance] = useState<LinkedInPerformanceRow[]>([]);
    const [meta, setMeta] = useState<LinkedInTexasMeta | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionError, setActionError] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [contentDraft, setContentDraft] = useState('');
    const [noteDraft, setNoteDraft] = useState('');
    const [reasonDraft, setReasonDraft] = useState('');
    const [rejecting, setRejecting] = useState(false);
    const [metricsDraft, setMetricsDraft] = useState<MetricsDraft>(emptyMetrics());
    const [engagementMessage, setEngagementMessage] = useState<string | null>(null);

    const load = useCallback(async (selectedDate: string) => {
        setLoading(true);
        setActionError(null);
        setRejecting(false);
        setEngagementMessage(null);
        try {
            const [p, perf, m] = await Promise.allSettled([
                linkedinOrganicApi.getPost(selectedDate),
                linkedinOrganicApi.getPerformance(),
                linkedinOrganicApi.getMeta(),
            ]);
            const next = p.status === 'fulfilled' ? p.value : null;
            setPost(next);
            setContentDraft(next?.content ?? '');
            setPerformance(perf.status === 'fulfilled' && Array.isArray(perf.value) ? perf.value : []);
            setMeta(m.status === 'fulfilled' ? m.value : null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(date); }, [date, load]);

    const actions = pendingActions(post?.status);

    const runAction = async (action: LinkedInReviewAction) => {
        if (!post) return;
        setBusy(action);
        setActionError(null);
        try {
            const updated = await linkedinOrganicApi.review(post.date, {
                action,
                note: noteDraft.trim() || undefined,
                reason: action === 'reject' ? reasonDraft.trim() || undefined : undefined,
                content: action === 'approve' && contentDraft.trim() ? contentDraft.trim() : undefined,
            });
            setPost(updated);
            setContentDraft(updated.content ?? '');
            setNoteDraft('');
            setReasonDraft('');
            setRejecting(false);
        } catch (e: any) {
            setActionError(e?.message || 'Review action failed — is the gate live?');
        } finally {
            setBusy(null);
        }
    };

    const generate = async () => {
        setBusy('generate');
        setActionError(null);
        try {
            const created = await linkedinOrganicApi.createPost({ date, generateDraft: true });
            setPost(created);
            setContentDraft(created.content ?? '');
        } catch (e: any) {
            setActionError(e?.message || 'Could not generate a draft — admin access required');
        } finally {
            setBusy(null);
        }
    };

    const recordEngagement = async () => {
        if (!post?.rotationKey) return;
        setBusy('engage');
        setActionError(null);
        setEngagementMessage(null);
        try {
            const metrics = {
                views: Number(metricsDraft.views) || 0,
                clicks: Number(metricsDraft.clicks) || 0,
                conversions: Number(metricsDraft.conversions) || 0,
            };
            const recorded = await linkedinOrganicApi.trackEngagement(post.rotationKey, metrics);
            setMetricsDraft(emptyMetrics());
            setEngagementMessage(
                `Recorded — views ${recorded?.views ?? metrics.views}, clicks ${recorded?.clicks ?? metrics.clicks}, conversions ${recorded?.conversions ?? metrics.conversions}`,
            );
            const perf = await linkedinOrganicApi.getPerformance();
            setPerformance(perf);
        } catch (e: any) {
            setActionError(e?.message || 'Could not record engagement');
        } finally {
            setBusy(null);
        }
    };

    const rotationName = post?.rotationKey
        ? meta?.rotation?.find((r) => r.key === post.rotationKey)?.name || post.templateName || post.rotationKey
        : post?.templateName || '—';

    return (
        <div className="max-w-7xl mx-auto px-4 py-8" data-testid="linkedin-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 mb-1">LinkedIn Texas Organic Engine</h1>
                    <p className="text-sm text-gray-500">
                        Daily organic post rotation, human review gate, and per-template engagement for the Texas accounts.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                        Post date
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => e.target.value && setDate(e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                            data-testid="linkedin-date"
                        />
                    </label>
                    <button onClick={() => load(date)} className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-50 text-sm">
                        Refresh
                    </button>
                </div>
            </div>

            {loading && <p className="text-gray-500" data-testid="linkedin-loading">Loading LinkedIn rotation…</p>}

            {actionError && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-4 mb-6" data-testid="linkedin-error">
                    {actionError}
                </div>
            )}

            {!loading && !post && (
                <div className="bg-gray-50 border border-gray-200 rounded p-8 text-center text-gray-500 mb-6" data-testid="linkedin-empty">
                    <p className="font-semibold text-gray-600 mb-1">No organic post for {date}</p>
                    <p className="text-sm mb-4">Generate the daily post and drive it through the review gate.</p>
                    <button
                        onClick={generate}
                        disabled={busy === 'generate'}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 text-sm font-semibold"
                        data-testid="linkedin-generate"
                    >
                        {busy === 'generate' ? 'Generating…' : 'Generate today’s post'}
                    </button>
                </div>
            )}

            {!loading && post && (
                <div className="bg-white rounded-xl shadow p-6 mb-6" data-testid="linkedin-post-card">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2 text-xs">
                            <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-medium">{rotationName}</span>
                            <span className="text-gray-400">{post.date}</span>
                            {post.updatedAt && <span className="text-gray-400">edited {post.updatedAt}</span>}
                        </div>
                        <span
                            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusBadgeClass(post.status)}`}
                            data-testid={`linkedin-status-${String(post.status).toUpperCase()}`}
                        >
                            {String(post.status).toUpperCase()}
                        </span>
                    </div>

                    {(actions.includes('approve') || actions.includes('publish')) && (
                        <textarea
                            value={contentDraft}
                            onChange={(e) => setContentDraft(e.target.value)}
                            rows={6}
                            aria-label="Post content"
                            className="border rounded-lg px-3 py-2 w-full text-sm focus:ring-2 focus:ring-green-500 mb-3"
                            data-testid="linkedin-content-editor"
                        />
                    )}

                    {!actions.includes('approve') && !actions.includes('publish') && (
                        <p className="text-sm text-gray-800 mb-3 whitespace-pre-line" data-testid="linkedin-content">
                            {post.content}
                        </p>
                    )}

                    {post.review?.decision && (
                        <p className="text-xs text-gray-500 mb-3" data-testid="linkedin-review-meta">
                            Last reviewed: {post.review.decision}
                            {post.review.reviewer ? ` by ${post.review.reviewer}` : ''}
                            {post.review.note ? ` — ${post.review.note}` : ''}
                            {post.review.reason ? ` — ${post.review.reason}` : ''}
                        </p>
                    )}

                    {actions.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                            {actions.map((action) => (
                                <button
                                    key={action}
                                    onClick={() => {
                                        if (action === 'reject') {
                                            setRejecting((v) => !v);
                                            return;
                                        }
                                        runAction(action);
                                    }}
                                    disabled={busy === action}
                                    className={action === 'publish'
                                        ? 'bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 disabled:opacity-50 text-sm font-medium'
                                        : action === 'approve'
                                            ? 'bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50 text-sm font-medium'
                                            : 'bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 disabled:opacity-50 text-sm font-medium'}
                                    data-testid={`linkedin-${action}`}
                                >
                                    {busy === action ? 'Working…' : action === 'approve' ? 'Approve' : action === 'publish' ? 'Publish' : 'Reject'}
                                </button>
                            ))}
                            <input
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                placeholder="Optional review note"
                                className="border rounded px-2 py-1 text-sm flex-1 min-w-40"
                                data-testid="linkedin-note"
                            />
                        </div>
                    )}

                    {rejecting && (
                        <input
                            value={reasonDraft}
                            onChange={(e) => setReasonDraft(e.target.value)}
                            placeholder="Rejection reason (shown in review log)"
                            className="border rounded px-3 py-2 text-sm w-full mb-2"
                            data-testid="linkedin-reason"
                        />
                    )}

                    {actions.includes('reject') && rejecting && (
                        <button
                            onClick={() => runAction('reject')}
                            disabled={!reasonDraft.trim() || busy === 'reject'}
                            className="bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 disabled:opacity-50 text-sm font-semibold mb-2"
                            data-testid="linkedin-reject-confirm"
                        >
                            Confirm rejection
                        </button>
                    )}
                </div>
            )}

            {!loading && post && post.rotationKey && (
                <div className="bg-white rounded-xl shadow p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-1">Engagement feedback</h2>
                    <p className="text-sm text-gray-500 mb-3">
                        Record tracked metrics onto the <strong>{rotationName}</strong> rotation lane.
                    </p>
                    <div className="grid gap-3 sm:grid-cols-4 mb-3">
                        {(['views', 'clicks', 'conversions'] as const).map((k) => (
                            <label key={k} className="text-xs text-gray-500">
                                {k[0].toUpperCase() + k.slice(1)}
                                <input
                                    type="number"
                                    min={0}
                                    value={metricsDraft[k]}
                                    onChange={(e) => setMetricsDraft((d) => ({ ...d, [k]: e.target.value }))}
                                    className="mt-1 border rounded px-2 py-1 w-full text-sm"
                                    data-testid={`linkedin-metric-${k}`}
                                />
                            </label>
                        ))}
                    </div>
                    <button
                        onClick={recordEngagement}
                        disabled={busy === 'engage'}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 text-sm font-semibold"
                        data-testid="linkedin-engage-save"
                    >
                        {busy === 'engage' ? 'Recording…' : 'Record engagement'}
                    </button>
                    {engagementMessage && (
                        <p className="text-sm text-green-700 mt-3" data-testid="linkedin-engagement-message">
                            {engagementMessage}
                        </p>
                    )}
                </div>
            )}

            {!loading && performance.length > 0 && (
                <div className="bg-white rounded-xl shadow overflow-hidden mb-6">
                    <h2 className="text-lg font-semibold px-6 pt-5 pb-1">Rotation performance</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="linkedin-performance">
                            <thead>
                                <tr className="text-left text-gray-500 border-b">
                                    <th className="py-2 px-6">Lane</th>
                                    <th className="py-2 px-3">Posts</th>
                                    <th className="py-2 px-3">Views</th>
                                    <th className="py-2 px-3">Clicks</th>
                                    <th className="py-2 px-3">Conversions</th>
                                    <th className="py-2 px-3">Engagement</th>
                                </tr>
                            </thead>
                            <tbody>
                                {performance.map((row, i) => (
                                    <tr key={row.rotationKey || i} className="border-b last:border-0" data-testid="linkedin-performance-row">
                                        <td className="py-2 px-6 font-medium text-gray-800">{row.name || row.rotationKey}</td>
                                        <td className="py-2 px-3">{row.posts ?? 0}</td>
                                        <td className="py-2 px-3">{row.views ?? 0}</td>
                                        <td className="py-2 px-3">{row.clicks ?? 0}</td>
                                        <td className="py-2 px-3">{row.conversions ?? 0}</td>
                                        <td className="py-2 px-3">{(row.engagementRate ?? 0)}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!loading && meta && (meta.rotation?.length || meta.statuses) && (
                <div className="bg-white rounded-xl shadow p-6">
                    <h2 className="text-lg font-semibold mb-3">Rotation meta</h2>
                    {Array.isArray(meta.rotation) && meta.rotation.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3" data-testid="linkedin-meta-rotation">
                            {meta.rotation.map((r) => (
                                <span key={r.key} className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-medium">
                                    {r.name || r.key}
                                </span>
                            ))}
                        </div>
                    )}
                    {meta.statuses && (
                        <p className="text-sm text-gray-500" data-testid="linkedin-meta-statuses">
                            Statuses: {Array.isArray(meta.statuses) ? meta.statuses.join(', ') : Object.keys(meta.statuses).join(', ')}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default LinkedInOrganicDashboard;